import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Navbar, NavbarBrand } from 'reactstrap';

import { resizeImage } from '@techchange/filestack';
import { IconButton, ENROLLMENT_TYPE, MEMBERSHIP_TYPE } from '@techchange/components';
import { messages } from '../../i18n';

import LoggedInNav from './LoggedInNav';
import LoggedOutNav from './LoggedOutNav';

import './UniversalNavbar.scss';

const propTypes = {

	/**
	 * The current user (should be defined if the user is logged in).
	 */
	currentUser: PropTypes.shape({

		/**
		 * The user's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The user's display name.
		 */
		display_name: PropTypes.string.isRequired,

		/**
		 * The profile pictures for the user.
		 */
		avatars: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * True, if the image is active.
			 */
			active: PropTypes.bool,

			/**
			 * The filstack image url.
			 */
			image: PropTypes.string,

			/**
			 * The filestack security policy.
			 */
			policy: PropTypes.string,

			/**
			 * The filestack security signature.
			 */
			signature: PropTypes.string,

		})).isRequired,

		/**
		 * The user's enrollment in the active course session (if any).
		 */
		enrollment: PropTypes.shape({

			/**
			 * The enrollment type.
			 */
			type: PropTypes.oneOf([
				ENROLLMENT_TYPE.GUEST,
				ENROLLMENT_TYPE.STUDENT,
				ENROLLMENT_TYPE.GUEST_EXPERT,
				ENROLLMENT_TYPE.FACILITATOR,
				ENROLLMENT_TYPE.CONTENT_EDITOR,
				ENROLLMENT_TYPE.ADMIN
			]),

		}),

		/**
		 * The user's membership in the organization.
		 */
		membership: PropTypes.shape({

			/**
			 * The membership type.
			 */
			type: PropTypes.oneOf([
				MEMBERSHIP_TYPE.STUDENT,
				MEMBERSHIP_TYPE.ADMIN,
				MEMBERSHIP_TYPE.OWNER,
			]),

		}).isRequired,

	}),

	/**
	 * The organization for the current user.
	 */
	myOrganization: PropTypes.shape({

		/**
		 * The organization's title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The organization's logo. Optional — an org may have no branding
		 * (null/empty), in which case the logo is simply not rendered.
		 */
		logo: PropTypes.string,

		/**
		 * The organization's logo link (if any).
		 */
		logo_link: PropTypes.string,

	}).isRequired,

	/**
	 * The active session (if any).
	 */
	session: PropTypes.shape({

		/**
		 * The session ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The session slug.
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * The active course (if any).
		 */
		course: PropTypes.shape({

			/**
			 * The course ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The course title.
			 */
			title: PropTypes.string.isRequired,

		}),

	}),

	/**
	 * The course slug (i.e. base URL + session slug) to use as prefix for session specific links.
	 */
	courseSlug: PropTypes.string,

	/**
	 * NOTE: Temporary flag to hide fields related to automatic
	 * translation.
	 */
	hideTranslationFields: PropTypes.bool,

	/**
	 * Callback to handle updating the user's language preference.
	 */
	onSelectLanguage: PropTypes.func,

};

/**
 * Shared component to render the TechChange Universal Navbar.
 *
 * @param   {object} props  The props.
 * @returns {object}        The rendered component.
 */
const UniversalNavbar = (props) => {

	const intl = useIntl();

	const {
		currentUser,
		myOrganization = {},
		session,
		courseSlug,
		hideTranslationFields,
		onSelectLanguage
	} = props;

	const isLoggedIn = Boolean(currentUser);

	const homeName = intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_HOME, { name: myOrganization.title });
	const logoName = intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_BRAND, { name: myOrganization.title });

	return (
		<div
			id="tc-universal-navbar"
			className={isLoggedIn ? 'tc-universal-navbar-logged-in' : 'tc-universal-navbar-logged-out'}>
			<Navbar expand={isLoggedIn}>
				{/* Toggler is used in farragut to inject the menu via a react portal */}
				{isLoggedIn &&
					<div id="tc-universal-navbar-logged-in-nav-mobile-custom-toggler-container" />
				}
				{/* Only render the logo when the org actually has one — otherwise
					resizeImage('') yields an empty src and the browser shows a
					broken-image icon. The home icon below still links home. */}
				{myOrganization.logo &&
					<NavbarBrand
						aria-label={homeName}
						className="img-link"
						href={myOrganization.logo_link || '/'}>
						<img
							src={resizeImage(myOrganization.logo, 'height', 75)}
							srcSet={`
								${resizeImage(myOrganization.logo, 'height', 75)} 1x,
								${resizeImage(myOrganization.logo, 'height', 150)} 2x
							`}
							alt={logoName} />
					</NavbarBrand>
				}
				<IconButton
					className="navbar-home-icon"
					icon="fal fa-home-alt fa-xl"
					tag={<a href={myOrganization.logo_link || '/'}>{' '}</a>}
					size="45px"
					title={homeName} />
				{!isLoggedIn &&
					<LoggedOutNav
						myOrganization={myOrganization} />
				}
				{isLoggedIn &&
					<LoggedInNav
						currentUser={currentUser}
						myOrganization={myOrganization}
						session={session}
						courseSlug={courseSlug}
						hideTranslationFields={hideTranslationFields}
						onSelectLanguage={onSelectLanguage} />
				}
			</Navbar>
		</div>
	);

};

UniversalNavbar.propTypes = propTypes;

export default UniversalNavbar;
